import React from "react";
import { Shield, Award, Target, Users, Wrench, CheckCircle, Lightbulb, Zap } from "lucide-react";
import { aboutContent } from "../mock";

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              About Nortec Integrated Services
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              {aboutContent.mission}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 lg:p-12 border border-blue-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Who We Are</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {aboutContent.description}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {aboutContent.approach}
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 lg:p-12 border border-gray-200">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Commitment</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                {aboutContent.compliance}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Strengths</h2>
            <p className="text-xl text-gray-600">
              The principles and capabilities that drive our success
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {aboutContent.values.map((value, index) => {
              const icons = [Shield, Lightbulb, Wrench, Target];
              const Icon = icons[index];
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow space-y-4"
                >
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Icon size={28} className="text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Industries & Sectors We Serve</h2>
            <p className="text-xl text-gray-600">
              Comprehensive expertise across multiple industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Renewable Energy (Solar, Wind, Hydrogen, BESS)",
              "Heavy Industry & Manufacturing",
              "Power Systems & Transmission",
              "Mining & Resources",
              "Construction & Infrastructure",
              "Residential & Domestic",
              "Commercial & SME",
              "Industrial Processing & Refineries",
              "Remote & Regional Asset Portfolios",
            ].map((sector, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-6 border border-blue-200 hover:border-blue-400 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="text-lg font-semibold text-gray-900">{sector}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Capabilities Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Core Capabilities</h2>
            <p className="text-xl text-gray-600">
              Integrated solutions across the entire asset lifecycle
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Target size={28} className="text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Operations & Maintenance</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  O&M framework development & implementation
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Asset lifecycle strategies
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  HV/LV electrical maintenance
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Preventative & corrective maintenance
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Contractor coordination & supervision
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Wrench size={28} className="text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Technical & Engineering</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Asset integrity procedures & documentation
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Controls engineering & SCADA support
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Hydrogen, solar & BESS assessments
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  System architecture design
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  RPEQ engineering coordination
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Users size={28} className="text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Advisory & Consulting</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Asset management strategy reviews
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Risk-based maintenance planning
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Safety system review & alignment
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Budget forecasting & cost planning
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Business-level operational advisory
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              Why Choose Nortec Integrated Services?
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <Zap size={40} className="text-white mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">Multidisciplinary Expertise</h3>
                <p className="text-blue-100">
                  Electrical, instrumentation, HV systems, O&M, testing, and project delivery capabilities under one roof
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <Award size={40} className="text-white mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">Specialized Knowledge</h3>
                <p className="text-blue-100">
                  Deep understanding of hydrogen, solar, wind, and BESS technologies with proven industry experience
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <Users size={40} className="text-white mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">Flexible Engagement Models</h3>
                <p className="text-blue-100">
                  On-call support, project-based work, retained advisory, or full O&M partnerships tailored to your needs
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <CheckCircle size={40} className="text-white mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">Proven Track Record</h3>
                <p className="text-blue-100">
                  Over a decade of industry experience delivering cost-effective, reliable solutions across Queensland and NT
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
